import React from 'react';
import { Navigate, Outlet, Route, Routes } from "react-router";
import {Upload, Gallery, Download, Timeline, Menu, DjRequests, BottomNav} from "./ui";
import { useSite } from "./store";

/**
 * Renders the gallery only when the wedding has it enabled. When
 * disabled the gallery does not exist for guests - any direct visit is
 * redirected home. This is the parent of the single-image download
 * permission; the zip download is independent of the gallery.
 */
function RequireGallery({ children }: { children: React.ReactElement }) {
  const { allowGallery } = useSite();
  return allowGallery ? children : <Navigate to="/" replace />;
}

/**
 * Renders the zip-download page only when the wedding has it enabled.
 * When disabled the page does not exist for guests — any direct visit
 * is redirected home.
 */
function RequireZipDownload({ children }: { children: React.ReactElement }) {
  const { allowZipDownload } = useSite();
  return allowZipDownload ? children : <Navigate to="/" replace />;
}

/**
 * Renders the timeline page only when the wedding has it enabled. When
 * disabled the page does not exist for guests — any direct visit is
 * redirected home.
 */
function RequireTimeline({ children }: { children: React.ReactElement }) {
  const { allowTimeline } = useSite();
  return allowTimeline ? children : <Navigate to="/" replace />;
}

/**
 * Renders the menu page only when the wedding has it enabled. When disabled
 * the page does not exist for guests — any direct visit is redirected home.
 */
function RequireMenu({ children }: { children: React.ReactElement }) {
  const { allowMenu } = useSite();
  return allowMenu ? children : <Navigate to="/" replace />;
}

/**
 * Renders the DJ requests page only when the wedding has it enabled. When
 * disabled the page does not exist for guests — any direct visit is
 * redirected home.
 */
function RequireDjRequests({ children }: { children: React.ReactElement }) {
  const { allowDjRequests } = useSite();
  return allowDjRequests ? children : <Navigate to="/" replace />;
}

/**
 * Persistent app shell. Renders the bottom navigation once and hosts the routed
 * page content through <Outlet/>, so the nav no longer mounts per page and stays
 * put across navigation. The `.layout` wrapper keeps the SCSS `:has()` hooks
 * (body bottom padding, toolbar offset, wide gallery grid) resolving against a
 * single root.
 */
function AppLayout() {
  return (
    <div className="layout">
      <Outlet />
      <BottomNav />
    </div>
  );
}

function App() {
  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route index element={<Upload />} />
        <Route
          path="gallery"
          element={
            <RequireGallery>
              <Gallery />
            </RequireGallery>
          }
        />
        <Route
          path="download"
          element={
            <RequireZipDownload>
              <Download />
            </RequireZipDownload>
          }
        />
        <Route
          path="timeline"
          element={
            <RequireTimeline>
              <Timeline />
            </RequireTimeline>
          }
        />
        <Route
          path="menu"
          element={
            <RequireMenu>
              <Menu />
            </RequireMenu>
          }
        />
        <Route
          path="dj-requests"
          element={
            <RequireDjRequests>
              <DjRequests />
            </RequireDjRequests>
          }
        />
      </Route>
    </Routes>
  );
}

export default App;
